/**
 * FE#484 — paused (takeover) chats. A chat is paused while a human holds it
 * (`takeover.status`); the agent stays silent there until the owner releases it.
 * Pure (no React) so the counting / bulk-resume rules are unit-tested.
 */
import type { MyBridgeAgentConversationRow } from '@common/reducers/backend-api/backend-api';

/** The conversations a human currently holds. */
export const pausedConversations = (rows: readonly MyBridgeAgentConversationRow[] | undefined): MyBridgeAgentConversationRow[] =>
  (rows ?? []).filter((r) => r.takeover?.status === true);

/**
 * The agent-row badge: how many chats are paused, or null when none. `more` = the
 * page was capped (`hasMore`), so the real count may be higher ("N+").
 */
export const pausedBadge = (
  rows: readonly MyBridgeAgentConversationRow[] | undefined,
  hasMore: boolean | undefined,
): { count: number; more: boolean } | null => {
  const count = pausedConversations(rows).length;
  return count > 0 ? { count, more: hasMore === true } : null;
};

/**
 * Resume several chats. One failing chat never blocks the rest; the failed chat
 * ids come back so the caller can say so.
 */
export async function resumeChats(chatIds: readonly string[], resume: (chatId: string) => Promise<unknown>): Promise<{ failed: string[] }> {
  const results = await Promise.allSettled(chatIds.map((id) => resume(id)));
  return { failed: chatIds.filter((_, i) => results[i].status === 'rejected') };
}
